package com.routexk.quiz.funnel

import com.routexk.quiz.core.config.AppConfig
import com.routexk.quiz.core.logger.getLogger
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.browser.window
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject

data class FunnelPlan(
    val uuid: String,
    val name: String,
    val amountCents: Long,
    val currency: String,
    val trialDays: Int?,
)

data class PaymentInit(
    val clientSecret: String,
    val publishableKey: String,
    val intentUuid: String?,
    val amountCents: Long?,
)

/** Result of intent/create: either a payable [init] or a backend error [code] (e.g. "already_subscribed"). */
data class IntentResult(
    val init: PaymentInit? = null,
    val errorCode: String? = null,
)

/**
 * Thin client over the existing RouteXK backend web-session API.
 * Every call is fail-soft: the funnel keeps working locally when the
 * backend is unreachable (except checkout, which genuinely needs it).
 */
object FunnelApi {
    private val logger = getLogger<FunnelApi>()

    /** Marker query param the backend matching rule keys on to serve this funnel's plans. */
    private const val FUNNEL_MARKER = "funnel=quizb2c"

    /** Appends [FUNNEL_MARKER] to a URL's query string if not already present. */
    private fun withFunnelMarker(url: String): String {
        if (url.contains("funnel=")) return url
        val separator = if (url.contains("?")) "&" else "?"
        // Keep any fragment (#…) after the query so the marker stays a real param.
        val hashIndex = url.indexOf('#')
        return if (hashIndex >= 0) {
            url.substring(0, hashIndex) + separator + FUNNEL_MARKER + url.substring(hashIndex)
        } else {
            url + separator + FUNNEL_MARKER
        }
    }

    private fun JsonObject.data(): JsonObject? =
        (get("data") as? JsonObject).takeIf {
            (get("status") as? JsonPrimitive)?.content == "success"
        }

    private fun JsonObject.string(key: String): String? =
        (get(key) as? JsonPrimitive)?.let { if (it.isString) it.content else it.content.takeIf { c -> c != "null" } }

    /** Creates a backend session on first call; reuses the stored uuid afterwards. */
    suspend fun ensureSession(client: HttpClient): String? {
        FunnelStore.sessionUuid?.let { return it }
        return try {
            val response = client.post(AppConfig.getApiUrl("session/create")) {
                contentType(ContentType.Application.Json)
                setBody(
                    buildJsonObject {
                        // Tag the session URL with a stable funnel marker so the
                        // backend's matching rule routes it to the Quiz B2C product
                        // view config (the 2/4/8-week trial packages) without
                        // changing the address bar the user sees.
                        put("raw_url", withFunnelMarker(window.location.href))
                        put("user_agent", window.navigator.userAgent)
                        put("referer", kotlinx.browser.document.referrer.ifEmpty { null })
                        // Local dev: the backend can't resolve a public client IP
                        // from a 127.0.0.1 connection, so pass a test address
                        if (window.location.hostname == "localhost") {
                            put("ip_address", "203.0.113.10")
                        }
                        putJsonObject("custom_data") { put("funnel", "xk-web") }
                    }
                )
            }
            val data = response.body<JsonObject>().data()
            // Backend resolves the visitor's city from their IP — prefill the quiz.
            data?.string("cityName")?.takeIf { it.isNotBlank() }?.let { city ->
                val region = (data.string("regionName") ?: data.string("region"))?.takeIf { it.isNotBlank() && it != city }
                FunnelStore.detectedCity = if (region != null) "$city, $region" else city
            }
            val uuid = data?.string("uuid")
            uuid?.also { FunnelStore.sessionUuid = it }
        } catch (e: Throwable) {
            logger.warn("session/create failed: ${e.message}")
            null
        }
    }

    suspend fun trackEvent(client: HttpClient, name: String) {
        val uuid = ensureSession(client) ?: return
        try {
            client.post(AppConfig.getApiUrl("session/event")) {
                contentType(ContentType.Application.Json)
                setBody(
                    buildJsonObject {
                        put("session_uuid", uuid)
                        put("name", name)
                    }
                )
            }
        } catch (e: Throwable) {
            logger.warn("session/event failed: ${e.message}")
        }
    }

    private suspend fun patchSession(client: HttpClient, body: JsonObject): Boolean {
        val uuid = ensureSession(client) ?: return false
        return try {
            client.patch(AppConfig.getApiUrl("sessions/$uuid")) {
                contentType(ContentType.Application.Json)
                setBody(body)
            }.status.isSuccess()
        } catch (e: Throwable) {
            logger.warn("session patch failed: ${e.message}")
            false
        }
    }

    /** Merges quiz answers into the session's custom_data. */
    suspend fun saveQuizAnswers(client: HttpClient, answers: FunnelAnswers) {
        patchSession(
            client,
            buildJsonObject {
                putJsonObject("custom_data") {
                    putJsonObject("quiz") {
                        answers.toCustomData().forEach { (k, v) -> put(k, v) }
                    }
                }
            }
        )
    }

    /**
     * Saves the email plus, in the same PATCH, the FB Pixel lead event_id and
     * _fbp/_fbc cookies — the backend fires the deduplicated CAPI Lead when it
     * sees the email land together with fb_lead_event_id.
     */
    suspend fun saveEmail(
        client: HttpClient,
        email: String,
        fbLeadEventId: String? = null,
        fbp: String? = null,
        fbc: String? = null,
    ): Boolean =
        patchSession(
            client,
            buildJsonObject {
                put("email", email)
                if (fbLeadEventId != null || fbp != null || fbc != null) {
                    putJsonObject("custom_data") {
                        fbLeadEventId?.let { put("fb_lead_event_id", it) }
                        fbp?.let { put("fbp", it) }
                        fbc?.let { put("fbc", it) }
                    }
                }
            }
        )

    /**
     * Stores FB Pixel identifiers in the session's custom_data so the Stripe
     * webhook / CAPI sender can deduplicate against the browser events and
     * improve match quality. Null values are skipped; no-op when all are null.
     */
    suspend fun saveFbData(client: HttpClient, vararg entries: Pair<String, String?>): Boolean {
        val present = entries.filter { it.second != null }
        if (present.isEmpty()) return true
        return patchSession(
            client,
            buildJsonObject {
                putJsonObject("custom_data") {
                    present.forEach { (k, v) -> put(k, v!!) }
                }
            }
        )
    }

    /**
     * Stores the GA4 client/session ids in the session's custom_data so the
     * backend can send the server-side purchase event (Measurement Protocol)
     * to the same GA4 user when the Stripe webhook fires.
     */
    suspend fun saveGaIds(client: HttpClient, clientId: String, sessionId: String?): Boolean =
        patchSession(
            client,
            buildJsonObject {
                putJsonObject("custom_data") {
                    put("ga_client_id", clientId)
                    sessionId?.let { put("ga_session_id", it) }
                }
            }
        )

    suspend fun fetchPlans(client: HttpClient): List<FunnelPlan> {
        val uuid = ensureSession(client) ?: return emptyList()
        return try {
            val response = client.get(AppConfig.getApiUrl("products")) {
                parameter("session_uuid", uuid)
            }
            val products = response.body<JsonObject>().data()?.get("products")?.jsonArray ?: return emptyList()
            products.mapNotNull { element ->
                val p = element.jsonObject
                FunnelPlan(
                    uuid = p.string("uuid") ?: return@mapNotNull null,
                    name = p.string("name") ?: "",
                    amountCents = (p["amountCents"] as? JsonPrimitive)?.longOrNull ?: return@mapNotNull null,
                    currency = p.string("currency") ?: "usd",
                    trialDays = (p["trialDays"] as? JsonPrimitive)?.content?.toIntOrNull(),
                )
            }
        } catch (e: Throwable) {
            logger.warn("products fetch failed: ${e.message}")
            emptyList()
        }
    }

    suspend fun createIntent(client: HttpClient, productUuid: String): IntentResult {
        val uuid = ensureSession(client) ?: return IntentResult()
        return try {
            val response = client.post(AppConfig.getApiUrl("intent/create")) {
                contentType(ContentType.Application.Json)
                setBody(
                    buildJsonObject {
                        put("session_uuid", uuid)
                        put("product_uuid", productUuid)
                    }
                )
            }
            val body = response.body<JsonObject>()
            val payment = body.data()?.get("payment")?.jsonObject
            if (payment == null) {
                logger.warn("intent/create rejected: ${body.string("message")}")
                // Error envelope: data() is null when status != success, so read the raw field
                return IntentResult(errorCode = (body["data"] as? JsonObject)?.string("code"))
            }
            val clientSecret = payment.string("clientSecret") ?: return IntentResult()
            val publishableKey = payment.string("publishableKey") ?: return IntentResult()
            IntentResult(
                init = PaymentInit(
                    clientSecret = clientSecret,
                    publishableKey = publishableKey,
                    intentUuid = payment.string("intentUuid"),
                    amountCents = (payment["amountCents"] as? JsonPrimitive)?.longOrNull,
                ).also { FunnelStore.intentUuid = it.intentUuid }
            )
        } catch (e: Throwable) {
            logger.warn("intent/create failed: ${e.message}")
            IntentResult()
        }
    }

    /** Returns the intent status reported by the backend, e.g. "succeeded". */
    suspend fun paymentStatus(client: HttpClient, intentUuid: String): String? {
        return try {
            val response = client.post(AppConfig.getApiUrl("payment/status")) {
                contentType(ContentType.Application.Json)
                setBody(
                    buildJsonObject {
                        put("intent_uuid", intentUuid)
                        FunnelStore.sessionUuid?.let { put("session_uuid", it) }
                    }
                )
            }
            response.body<JsonObject>().data()?.string("status")
        } catch (e: Throwable) {
            logger.warn("payment/status failed: ${e.message}")
            null
        }
    }

    /**
     * Returns the per-session AppsFlyer deep link for the paid session, so the
     * success screen can auto-connect the mobile app to the just-created account
     * + subscription (via deep_link_sub2=<session_uuid>). Null if unavailable.
     */
    suspend fun fetchDeepLink(client: HttpClient, intentUuid: String?): String? {
        val session = FunnelStore.sessionUuid ?: return null
        return try {
            val response = client.post(AppConfig.getApiUrl("payment/status")) {
                contentType(ContentType.Application.Json)
                setBody(
                    buildJsonObject {
                        intentUuid?.let { put("intent_uuid", it) }
                        put("session_uuid", session)
                    }
                )
            }
            response.body<JsonObject>().data()?.string("mobileDeepLink")?.takeIf { it.isNotBlank() }
        } catch (e: Throwable) {
            logger.warn("deep link fetch failed: ${e.message}")
            null
        }
    }
}
